<template>
    <aside class="sidebar" :class="{
        'sidebar--visible': mobileVisible,
        'sidebar--pinned': isPinned
    }">
        <div class="sidebar__logo">
            <img src="@/assets/logo-CorWfPtl.png" alt="Logo" />
        </div>

        <nav class="sidebar__nav">
            <ul>
                <li v-for="(group, index) in groupedMenuItems" :key="index">
                    <div class="sidebar__category">{{ group.category }}</div>
                    <ul>
                        <li v-for="item in group.items" :key="item.text">
                            <router-link :to="item.path" active-class="active" class="sidebar__item"
                                @click="$emit('close')">
                                <font-awesome-icon :icon="item.icon" />
                                <span class="sidebar__text">{{ item.text }}</span>
                            </router-link>
                        </li>
                    </ul>
                    <hr class="sidebar__separator" />
                </li>
            </ul>

            <ul class="sidebar__bottom">
                <li class="sidebar__pin">
                    <button class="sidebar__item" type="button" @click="togglePin">
                        <font-awesome-icon :icon="isPinned ? ['fas', 'lock'] : ['fas', 'unlock']" />
                        <span class="sidebar__text">
                            {{ isPinned ? 'Odepnij pasek' : 'Przypnij pasek' }}
                        </span>
                    </button>
                </li>

                <li>
                    <button class="sidebar__item" type="button" @click="handleLogout">
                        <font-awesome-icon :icon="['fas', 'sign-out-alt']" />
                        <span class="sidebar__text">Wyloguj</span>
                    </button>
                </li>
            </ul>
        </nav>
    </aside>
</template>

<script>
import { mapStores, mapActions } from 'pinia'
import { useAuthStore } from '@/stores/authStore'

export default {
    name: 'CmsSidebar',
    props: {
        mobileVisible: { type: Boolean, default: false }
    },
    data() {
        return {
            LS_KEY: 'sidebarPinned',
            isPinned: true,
            groupedMenuItems: [
                {
                    category: 'Zarządzanie',
                    items: [
                        { icon: ['fas', 'chart-line'], text: 'Panel główny', path: '/cms/dashboard' },
                        { icon: ['fas', 'box-open'], text: 'Zamówienia', path: '/cms/orders' }
                    ]
                },
                {
                    category: 'Lawety',
                    items: [
                        { icon: ['fas', 'trailer'], text: 'Lawety', path: '/cms/trailers' },
                        { icon: ['fas', 'layer-group'], text: 'Grupy lawet', path: '/cms/trailer-groups' }
                    ]
                },
                {
                    category: 'Dodatki',
                    items: [
                        { icon: ['fas', 'puzzle-piece'], text: 'Dodatki', path: '/cms/addons' },
                        { icon: ['fas', 'plus-square'], text: 'Dodatki dla modeli', path: '/cms/model-addons' }
                    ]
                },
                {
                    category: 'Teksty stron',
                    items: [
                        { icon: ['fas', 'pen-to-square'], text: 'Teksty stron', path: '/cms/texts' }
                    ]
                },
                {
                    category: 'Ustawienia',
                    items: [
                        { icon: ['fas', 'gear'], text: 'Ustawienia', path: '/cms/settings' }
                    ]
                }
            ]
        }
    },
    computed: {
        ...mapStores(useAuthStore)
    },
    created() {
        try {
            const saved = localStorage.getItem(this.LS_KEY)
            if (saved !== null) this.isPinned = saved === 'true'
        } catch {
            // ignore
        }
    },
    watch: {
        isPinned(val) {
            try {
                localStorage.setItem(this.LS_KEY, String(val))
            } catch {
                // ignore
            }
        }
    },
    methods: {
        ...mapActions(useAuthStore, ['logout']),
        togglePin() {
            this.isPinned = !this.isPinned
        },
        async handleLogout() {
            const confirmLogout = window.confirm('Czy na pewno chcesz się wylogować?')
            if (!confirmLogout) return
            await this.logout()
            this.$router.push('/cms/login')
        }
    }
}
</script>

<style scoped lang="scss">
@use "sass:color";

.sidebar {
    width: 70px;
    min-width: 70px;
    max-width: 280px;
    max-height: 89.5vh;
    color: $color-text;
    box-shadow: 2px 0 10px rgba(0, 0, 0, .1);
    display: flex;
    flex-direction: column;
    z-index: 100;
    transition: width .3s ease;
    overflow: hidden;

    &:hover {
        width: 280px;
    }

    &--pinned {
        width: 280px !important;
        min-width: 280px;

        &:hover {
            width: 280px;
        }

        .sidebar__item .sidebar__text {
            opacity: 1 !important;
        }

        .sidebar__category {
            opacity: .6 !important;
        }
    }

    &__logo {
        padding: 1rem;
        margin: 1rem 0rem;
        display: flex;
        justify-content: center;
        align-items: center;

        img {
            max-width: 100%;
            height: auto;
            object-fit: contain;
            transition: transform .3s ease;
        }
    }

    &__nav {
        display: flex;
        flex-direction: column;
        flex-grow: 1;
        overflow-y: auto;
        overflow-x: hidden;
        padding: 0 .5rem;

        ul {
            list-style: none;
            padding: 0;
            margin: 0;

            li {
                margin-bottom: .5rem;
            }
        }

        .sidebar__bottom {
            margin-top: auto;
        }
    }

    &__category {
        font-size: .8rem;
        font-weight: 600;
        color: $color-text;
        padding: .75rem 1rem .25rem 1rem;
        text-transform: uppercase;
        letter-spacing: .05em;
        opacity: 0;
        transition: opacity .2s ease;

        .sidebar:hover & {
            opacity: .6;
        }
    }

    &__separator {
        border: none;
        border-top: 1px solid rgba(0, 0, 0, .1);
        margin: .5rem 1rem;
    }

    &__item {
        display: flex;
        align-items: center;
        gap: 1rem;
        padding: .75rem 1rem;
        color: $color-text-light;
        font-size: 1.05rem;
        border: none;
        background: none;
        width: 100%;
        text-align: left;
        border-left: 4px solid transparent;
        white-space: nowrap;
        overflow: hidden;
        cursor: pointer;
        transition: all .2s ease;
        text-decoration: none;

        svg {
            width: 20px;
            height: 20px;
            color: $color-text-light;
            flex-shrink: 0;
        }

        .sidebar__text {
            opacity: 0;
            transition: opacity .2s ease;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            display: inline-block;
            flex-grow: 1;
        }

        &:hover {
            background-color: color.adjust($color-primary, $lightness: 45%);
            color: $color-primary-dark;

            svg {
                color: $color-primary;
            }
        }

        .sidebar:hover & .sidebar__text {
            opacity: 1;
        }
    }

    .active {
        background-color: color.adjust($color-primary, $lightness: 45%);
        color: $color-primary-dark;
        border-left: 4px solid $color-primary;
        font-weight: 500;

        svg {
            color: $color-primary;
        }
    }

    @media (max-width: 768px) {
        position: absolute;
        left: -280px;
        top: 0;
        width: 280px !important;
        min-width: 280px;
        max-height: 100vh;
        height: 100%;
        padding-bottom: 40px;
        transition: left .3s ease;
        background-color: $color-background;
        z-index: 100;
        overflow-x: hidden;

        &.sidebar--visible {
            left: 0;
        }

        &:hover {
            width: 280px;
        }

        .sidebar__item .sidebar__text {
            opacity: 1 !important;
        }

        .sidebar__category {
            opacity: .6 !important;
        }

        .sidebar__pin {
            display: none;
        }
    }
}

.sidebar__pin {
    display: block;
}
</style>